/* The companion in the margin.
 *
 * A conversation beside the page — a word you can't reach for, a fact you need,
 * something you said three chapters ago and can't find. It never touches a
 * document. There is no mechanism by which it could: nothing here writes, and
 * the endpoint it talks to has no access to the store. The writer writes.
 *
 * This file is the client half — what Claude is told about the space, and how
 * the reply arrives. The prompt itself lives on the server, where the writer's
 * own browser cannot rewrite it.
 */

import { api } from './desktop.js';
import { authorization } from './identity.js';

/* Everything Claude is told about the space, in characters. Roughly twelve
   thousand tokens: enough for the open chapter in full and a real look at the
   rest, small enough that a long book does not become a long bill. */
export const CONTEXT_BUDGET = 48_000;
const OPEN_DOC_SHARE = 0.5;   // the page in front of them matters most
const NOTES_SHARE = 0.2;
const MIN_OTHER_DOC = 400;    // below this an excerpt says nothing

/** How much of a conversation is carried back each turn. */
export const MAX_TURNS = 24;
export const MAX_MESSAGE_CHARS = 8_000;

const words = t => { const m = (t || '').trim().match(/\S+/g); return m ? m.length : 0; };

/** Cut to `limit`, on a line break where there is one to be had. */
function clip(text, limit) {
  const t = (text || '').trim();
  if (t.length <= limit) return t;
  const cut = t.slice(0, limit);
  const brk = cut.lastIndexOf('\n');
  return (brk > limit * 0.6 ? cut.slice(0, brk) : cut).trimEnd() + '\n[…]';
}

/**
 * The space, written out for Claude.
 *
 * The open document goes in whole where it fits, because that is what the
 * question is usually about. The others go in as much as the budget allows —
 * excerpts, not summaries: "what did I call the harbourmaster" is answered by
 * the prose, and a synopsis would have dropped the name.
 */
export function buildContext(space, doc, { budget = CONTEXT_BUDGET } = {}) {
  if (!space) return '';
  const out = [];
  const head = [space.name || 'Untitled space', space.kind && '(' + space.kind + ')']
    .filter(Boolean).join(' ');
  out.push('# ' + head);
  if (space.description) out.push(space.description.trim());

  const others = space.docs.filter(d => d.id !== doc?.id);
  const notes = space.notes.filter(n => (n.title || n.body || '').trim());

  let left = budget - out.join('\n\n').length;

  if (doc) {
    const body = clip(doc.content, Math.max(MIN_OTHER_DOC, Math.round(budget * OPEN_DOC_SHARE)));
    out.push('## Open document: ' + (doc.title || 'Untitled') + '\n\n' + (body || '(empty so far)'));
    left -= body.length;
  }

  if (notes.length) {
    const each = Math.max(200, Math.round(budget * NOTES_SHARE / notes.length));
    const lines = notes.map(n =>
      '- ' + (n.kind === 'character' ? 'Character' : 'World') + ': ' +
      (n.title || 'Untitled') + ' — ' + (clip(n.body, each) || 'no notes yet'));
    out.push('## Reference notes\n\n' + lines.join('\n'));
    left -= lines.join('\n').length;
  }

  if (others.length) {
    // What is left, split evenly. A document too small to say anything is left
    // as a title and a word count, which at least tells Claude it exists.
    const each = Math.floor(Math.max(0, left) / others.length);
    const parts = others.map(d => {
      const head = '### ' + (d.title || 'Untitled') + ' (' + words(d.content) + ' words)';
      const syn = d.synopsis ? '\n' + d.synopsis.trim() : '';
      if (each < MIN_OTHER_DOC) return head + syn;
      return head + syn + '\n\n' + (clip(d.content, each) || '(empty so far)');
    });
    out.push('## Other documents in this space\n\n' + parts.join('\n\n'));
  }

  return out.join('\n\n');
}

/**
 * The tail of the conversation, within the limits the endpoint enforces.
 * Dropping the oldest turns is the honest failure: the recent exchange is what
 * the next question is about, and a summarised history would be a fiction.
 */
export function trimHistory(messages) {
  return messages
    .slice(-MAX_TURNS)
    .map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', text: clip(m.text, MAX_MESSAGE_CHARS) }))
    .filter(m => m.text);
}

/* ------------------------------------------------------------- transport */

/**
 * Read newline-delimited JSON off a stream.
 *
 * The reply is streamed because thinking and a web search together can take
 * half a minute, and half a minute of nothing reads as broken. One JSON object
 * per line: unambiguous, and an error mid-stream is still a message rather than
 * text that looks like part of the answer.
 */
export async function* readEvents(body) {
  const reader = body.getReader();
  const decode = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decode.decode(value, { stream: true });
    let nl;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line) continue;
      try { yield JSON.parse(line); } catch (e) { /* a torn line is not an answer */ }
    }
  }
  const last = buffer.trim();
  if (last) { try { yield JSON.parse(last); } catch (e) {} }
}

/** Talk to `/api/chat`, carrying the Identity token the way sync does. */
export function createChat({ fetchImpl = fetch, auth = authorization, url = api('/api/chat') } = {}) {
  return {
    /**
     * Ask, and stream the answer back through `onEvent`.
     * Resolves with the whole reply; rejects if nothing could be said at all.
     */
    async ask({ messages, context = '', signal, onEvent = () => {} }) {
      const res = await fetchImpl(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: await auth() },
        body: JSON.stringify({ messages: trimHistory(messages), context }),
        signal
      });

      if (!res.ok || !res.body) {
        let msg = 'The companion could not be reached.';
        try { msg = (await res.json()).error || msg; } catch (e) {}
        throw new Error(msg);
      }

      let text = '';
      let failure = null;
      for await (const ev of readEvents(res.body)) {
        if (ev.t === 'text') { text += ev.v; onEvent({ t: 'text', v: ev.v, text }); }
        else if (ev.t === 'error') failure = ev.v;
        else onEvent(ev);
      }

      // An error after some of the answer has arrived keeps the answer: the
      // writer can see what there was, and decide whether to ask again.
      if (failure && !text) throw new Error(failure);
      if (failure) onEvent({ t: 'error', v: failure });
      return text;
    }
  };
}
